import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import type {PosterTypeDimensions, PosterTypeObject} from '@PosterWhiteboard/poster-type/poster-type.types';
import {isPageVideo, pageObjectWithNewUids} from '@Libraries/page-object.library';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {ImageItemSource} from '@Libraries/image-item.library';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import {UserVideoSource} from '@Libraries/user-video-library';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {roundToTwoDecimals} from '@Utils/math.util';
import {v4 as uuidv4} from 'uuid';

const MM_PER_INCH = 25.4;
const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;

export enum PosterTypeUnits {
  PX = 'px',
  INCH = 'in',
  CM = 'cm',
  MM = 'mm',
  FT = 'ft',
}

export const doesMultipagePosterHavePagesAsTemplates = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    const templateId = pageObject.templateData?.posterId;
    if (templateId && templateId === posterObject.id) {
      return true;
    }
  }
  return false;
};

export const isPosterVideo = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (isPageVideo(pageObject)) {
      return true;
    }
  }

  return false;
};

export const doesPosterMeetGettyRequirements = (posterObject: PosterLoadObject): boolean => {
  if (!hasGettyContent(posterObject)) {
    return true;
  }

  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (doesPageHaveNonGettyContent(pageObject)) {
      return true;
    }
  }

  return false;
};

const doesPageHaveNonGettyContent = (pageObject: PageObject): boolean => {
  for (const [, item] of Object.entries(pageObject.items.itemsHashMap)) {
    if (item.gitype !== ITEM_TYPE.IMAGE && item.gitype !== ITEM_TYPE.VIDEO && item.gitype !== ITEM_TYPE.SLIDESHOW) {
      return true;
    }

    if (item.gitype === ITEM_TYPE.IMAGE) {
      const imageItem = item as ImageItemObject;
      if (
        imageItem.imageSource !== ImageItemSource.GETTY &&
        imageItem.imageSource !== ImageItemSource.GETTY_ILLUSTRATIONS &&
        imageItem.imageSource !== ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
      ) {
        return true;
      }
    }
    if (item.gitype === ITEM_TYPE.VIDEO) {
      const videoItem = item as VideoItemObject;
      if (videoItem.videoSource !== UserVideoSource.GETTY) {
        return true;
      }
    }

    if (item.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = item as SlideshowItemObject;
      for (const [, slideItem] of Object.entries(slideshowItem.slides.slidesHashMap)) {
        if (slideItem.gitype === ITEM_TYPE.IMAGESLIDE) {
          const slideImageItem = slideItem as ImageSlideItemObject;
          if (
            slideImageItem.imageSource !== ImageItemSource.GETTY &&
            slideImageItem.imageSource !== ImageItemSource.GETTY_ILLUSTRATIONS &&
            slideImageItem.imageSource !== ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
          ) {
            return true;
          }
        }

        if (slideItem.gitype === ITEM_TYPE.VIDEOSLIDE) {
          const slideVideoItem = slideItem as VideoSlideItemObject;
          if (slideVideoItem.videoSource !== UserVideoSource.GETTY) {
            return true;
          }
        }
      }
    }
  }
  return false;
};

const doesPageHaveGettyContent = (pageObject: PageObject): boolean => {
  for (const [, item] of Object.entries(pageObject.items.itemsHashMap)) {
    if (item.gitype === ITEM_TYPE.IMAGE) {
      const imageItem = item as ImageItemObject;
      if (
        imageItem.imageSource === ImageItemSource.GETTY ||
        imageItem.imageSource === ImageItemSource.GETTY_ILLUSTRATIONS ||
        imageItem.imageSource === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
      ) {
        return true;
      }
    }
    if (item.gitype === ITEM_TYPE.VIDEO) {
      const videoItem = item as VideoItemObject;
      if (videoItem.videoSource === UserVideoSource.GETTY) {
        return true;
      }
    }

    if (item.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = item as SlideshowItemObject;
      for (const [, slideItem] of Object.entries(slideshowItem.slides.slidesHashMap)) {
        if (slideItem.gitype === ITEM_TYPE.IMAGESLIDE) {
          const slideImageItem = slideItem as ImageSlideItemObject;
          if (
            slideImageItem.imageSource === ImageItemSource.GETTY ||
            slideImageItem.imageSource === ImageItemSource.GETTY_ILLUSTRATIONS ||
            slideImageItem.imageSource === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
          ) {
            return true;
          }
        }

        if (slideItem.gitype === ITEM_TYPE.VIDEOSLIDE) {
          const slideVideoItem = slideItem as VideoSlideItemObject;
          if (slideVideoItem.videoSource === UserVideoSource.GETTY) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

export const hasGettyContent = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (doesPageHaveGettyContent(pageObject)) {
      return true;
    }
  }
  return false;
};

export const isCustomSize = (name: string): boolean => {
  return name === 'poster-custom' || name === 'poster-custom-print' || name.includes('poster-custom-user');
};

export const getUnitsFromPosterTypeObject = (posterObject: PosterUnitsSource): PosterTypeUnits => {
  if (isCustomSize(posterObject.type.name)) {
    return normalizePosterUnits(posterObject.units);
  }
  return normalizePosterUnits(posterObject.type.units);
};

/** The fields that decide a poster's unit of measure and its size in that unit; both the whiteboard `Poster` and a `PosterLoadObject` have them. */
export interface PosterUnitsSource {
  width: number;
  height: number;
  units: string | null;
  userWidth: number | string | null;
  userHeight: number | string | null;
  type: Pick<PosterTypeObject, 'name' | 'units' | 'width' | 'height' | 'widthInch' | 'heightInch' | 'widthCm' | 'heightCm' | 'widthMm' | 'heightMm'>;
}

const POSTER_UNIT_ALIASES: Record<string, PosterTypeUnits> = {
  inch: PosterTypeUnits.INCH,
  inches: PosterTypeUnits.INCH,
  in: PosterTypeUnits.INCH,
  cm: PosterTypeUnits.CM,
  mm: PosterTypeUnits.MM,
  ft: PosterTypeUnits.FT,
  px: PosterTypeUnits.PX,
};

const normalizePosterUnits = (units: string | null | undefined): PosterTypeUnits => POSTER_UNIT_ALIASES[(units ?? '').trim().toLowerCase()] ?? PosterTypeUnits.PX;

/** Decimal places a length in each unit is shown with, in the editor's readouts and the download sizes alike. */
const POSTER_UNIT_PRECISION: Record<PosterTypeUnits, number> = {
  [PosterTypeUnits.PX]: 0,
  [PosterTypeUnits.INCH]: 2,
  [PosterTypeUnits.CM]: 1,
  [PosterTypeUnits.MM]: 1,
  [PosterTypeUnits.FT]: 2,
};

const UNITS_PER_INCH: Record<PosterTypeUnits, number> = {
  [PosterTypeUnits.PX]: 1,
  [PosterTypeUnits.INCH]: 1,
  [PosterTypeUnits.CM]: CM_PER_INCH,
  [PosterTypeUnits.MM]: MM_PER_INCH,
  [PosterTypeUnits.FT]: 1 / INCHES_PER_FOOT,
};

const roundToUnitPrecision = (value: number, units: PosterTypeUnits): number => Number(value.toFixed(POSTER_UNIT_PRECISION[units]));

/** A canvas-pixel length in the poster's unit, rounded for display, e.g. `0.75in`, `19.1mm`, `63px`. */
export const formatPixelLengthInPosterUnits = (poster: PosterUnitsSource, pixels: number): string => {
  return `${formatPixelLengthValueInPosterUnits(poster, pixels)}${getUnitsFromPosterTypeObject(poster)}`;
};

/** The length alone, rounded to the poster unit's precision, for labels where the unit goes without saying. */
export const formatPixelLengthValueInPosterUnits = (poster: PosterUnitsSource, pixels: number): string => {
  const units = getUnitsFromPosterTypeObject(poster);
  return (pixels * getPosterUnitsPerPixel(poster)).toFixed(POSTER_UNIT_PRECISION[units]);
};

/**
 * How many of the poster's units one canvas pixel spans. The canvas is not at the type's print DPI, so the
 * ratio comes from the poster's size in its unit against its size in canvas pixels. The two sizes are
 * compared on their longer edges: a type's pixel size can be stored the other way round from its size in
 * units, and a custom poster rotated in the editor swaps its canvas size but not the size the user typed.
 */
const getPosterUnitsPerPixel = (poster: PosterUnitsSource): number => {
  const units = getUnitsFromPosterTypeObject(poster);
  if (units === PosterTypeUnits.PX) {
    return 1;
  }
  if (isCustomSize(poster.type.name)) {
    const sizeInUnits = getCustomPosterSizeInUnits(poster);
    return Math.max(sizeInUnits.width, sizeInUnits.height) / Math.max(poster.width, poster.height);
  }
  const sizeInUnits = getPosterTypeSizeInUnits(poster.type, units);
  return Math.max(sizeInUnits.width, sizeInUnits.height) / Math.max(poster.type.width, poster.type.height);
};

/** The size the user typed for a custom poster; older posters carry it as strings. */
const getCustomPosterSizeInUnits = (poster: PosterUnitsSource): PosterTypeDimensions => ({width: Number(poster.userWidth), height: Number(poster.userHeight)});

const getPosterTypeSizeInUnits = (type: PosterUnitsSource['type'], units: PosterTypeUnits): PosterTypeDimensions => {
  switch (units) {
    case PosterTypeUnits.CM:
      return {width: type.widthCm, height: type.heightCm};
    case PosterTypeUnits.MM:
      return {width: type.widthMm, height: type.heightMm};
    case PosterTypeUnits.FT:
      return {width: type.widthInch / INCHES_PER_FOOT, height: type.heightInch / INCHES_PER_FOOT};
    case PosterTypeUnits.INCH:
      return {width: type.widthInch, height: type.heightInch};
    case PosterTypeUnits.PX:
    default:
      return {width: type.width, height: type.height};
  }
};

export const getPosterWidthAndHeight = (posterObject: PosterLoadObject): PosterTypeDimensions => {
  if (isCustomSize(posterObject.type.name)) {
    return getCustomPosterSizeInUnits(posterObject);
  }
  return getPosterTypeSizeInUnits(posterObject.type, getUnitsFromPosterTypeObject(posterObject));
};

const getPixelValueForDPI = (
  width: number,
  height: number,
  units: PosterTypeUnits,
  dpi: number
): {
  width: number;
  height: number;
} => {
  let widthInInches: number;
  let heightInInches: number;
  switch (units) {
    case PosterTypeUnits.INCH:
      widthInInches = roundToTwoDecimals(width);
      heightInInches = roundToTwoDecimals(height);
      break;

    case PosterTypeUnits.MM:
      widthInInches = roundToTwoDecimals(width / MM_PER_INCH);
      heightInInches = roundToTwoDecimals(height / MM_PER_INCH);
      break;

    case PosterTypeUnits.CM:
      widthInInches = roundToTwoDecimals(width / CM_PER_INCH);
      heightInInches = roundToTwoDecimals(height / CM_PER_INCH);
      break;

    case PosterTypeUnits.FT:
      widthInInches = roundToTwoDecimals(width * INCHES_PER_FOOT);
      heightInInches = roundToTwoDecimals(height * INCHES_PER_FOOT);
      break;
    case PosterTypeUnits.PX:
    default:
      return {width: width, height: height};
  }

  return {
    width: Math.round(widthInInches * dpi),
    height: Math.round(heightInInches * dpi),
  };
};

export const getWidthAndHeightInPixels = (posterObject: PosterLoadObject): {width: number; height: number} => {
  const {width, height} = getPosterWidthAndHeight(posterObject);
  return getPixelValueForDPI(width, height, getUnitsFromPosterTypeObject(posterObject), posterObject.type.dpi);
};

export const convertPixelsToPosterUnits = (dpi: number, posterObject: PosterLoadObject, pixelWidth: number, pixelHeight: number): PosterTypeDimensions => {
  const units = getUnitsFromPosterTypeObject(posterObject);
  if (units === PosterTypeUnits.PX) {
    return {width: Math.round(pixelWidth), height: Math.round(pixelHeight)};
  }
  return {
    width: roundToUnitPrecision((pixelWidth / dpi) * UNITS_PER_INCH[units], units),
    height: roundToUnitPrecision((pixelHeight / dpi) * UNITS_PER_INCH[units], units),
  };
};

export const processThumbnailsFromArrayToHashmap = (pageIdsInOrder: string[], thumbnailsInOrder: string[]): Record<string, string> => {
  const hashmap: Record<string, string> = {};

  for (let i = 0; i < pageIdsInOrder.length; i++) {
    hashmap[pageIdsInOrder[i]] = thumbnailsInOrder[i];
  }

  return hashmap;
};

export const isPosterMultipage = (posterObject: PosterLoadObject): boolean => {
  return posterObject.pages.pageOrder.length > 1;
};

export const posterLoadObjectWithNewUids = (posterObject: PosterLoadObject): PosterLoadObject => {
  const newPagesHashMap: Record<string, PageObject> = {};
  const newPageOrder: string[] = [];
  const pageIdMap = new Map<string, string>();

  for (const oldPageId of posterObject.pages.pageOrder) {
    const newPageId = uuidv4();
    pageIdMap.set(oldPageId, newPageId);
    newPageOrder.push(newPageId);
  }

  for (const [oldPageId, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    const newPageId = pageIdMap.get(oldPageId);
    if (!newPageId) continue;

    const pageWithNewUids = pageObjectWithNewUids(pageObject);
    newPagesHashMap[newPageId] = {
      ...pageWithNewUids,
      hashedID: newPageId,
    };
  }

  return {
    ...posterObject,
    pages: {
      pagesHashMap: newPagesHashMap,
      pageOrder: newPageOrder,
    },
  };
};

export const isExpensiveVideoPoster = (posterObject: PosterLoadObject): boolean => {
  for (const [, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    for (const [, item] of Object.entries(pageObject.items.itemsHashMap)) {
      if (item.gitype === ITEM_TYPE.VIDEO) {
        return true;
      }
      if (item.gitype === ITEM_TYPE.SLIDESHOW) {
        const slideshowItem = item as SlideshowItemObject;
        for (const [, slideItem] of Object.entries(slideshowItem.slides.slidesHashMap)) {
          if (slideItem.gitype === ITEM_TYPE.VIDEOSLIDE) {
            return true;
          }
        }
      }
    }
  }

  return false;
};
